import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle, ArrowLeft, Loader } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { supabase } from "@/integrations/supabase/client";

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const { checkSubscription, subscriptionTier } = useSubscription();
  const [verifying, setVerifying] = useState(false);
  const [verificationComplete, setVerificationComplete] = useState(false);
  const [verifiedTier, setVerifiedTier] = useState<string | null>(null);
  const [readyToNavigate, setReadyToNavigate] = useState(false);
  const verificationStarted = useRef(false);

  useEffect(() => {
    // Prevent multiple simultaneous verifications
    if (verificationStarted.current) return;
    
    const verifyPaymentAndRefreshSubscription = async () => {
      const sessionId = searchParams.get('session_id');
      
      if (!sessionId) {
        toast({
          title: "Payment Successful!",
          description: "Your subscription has been activated successfully.",
        });
        setVerificationComplete(true);
        setReadyToNavigate(true);
        return;
      }

      verificationStarted.current = true;
      setVerifying(true);
      
      try {
        console.log('[PaymentSuccess] Verifying payment with session ID:', sessionId);
        
        // Verify payment with Stripe
        const { data, error } = await supabase.functions.invoke('verify-payment', {
          body: { sessionId }
        });

        if (error) {
          console.error('[PaymentSuccess] Payment verification error:', error);
        } else if (data?.success) {
          console.log('[PaymentSuccess] Payment verified successfully:', data);
          setVerifiedTier(data.subscription_tier);
          const tierName = data.subscription_tier || 'Starter';
          
          // Show toast ONCE
          toast({
            title: "Payment Verified!",
            description: `Your subscription has been activated successfully. All ${tierName} features are now unlocked.`,
          });
          
          // Clear all cache
          const { data: { user } } = await supabase.auth.getUser();
          if (user) {
            console.log('[PaymentSuccess] Clearing cache for user:', user.id);
            sessionStorage.clear();
            localStorage.removeItem('subscription_cache');
            localStorage.removeItem('feature_access_cache');
          }
          
          // Retry subscription check with delays to ensure database propagation
          console.log('[PaymentSuccess] Starting subscription refresh with retries...');
          let attempts = 0;
          const maxAttempts = 3;
          
          while (attempts < maxAttempts) {
            await checkSubscription();
            console.log(`[PaymentSuccess] Subscription check attempt ${attempts + 1}/${maxAttempts}`);
            
            // Check if subscription was loaded successfully
            if (subscriptionTier === data.subscription_tier) {
              console.log('[PaymentSuccess] Subscription verified and loaded successfully');
              break;
            }
            
            // Wait before retrying
            attempts++;
            if (attempts < maxAttempts) {
              console.log('[PaymentSuccess] Waiting 1 second before retry...');
              await new Promise(resolve => setTimeout(resolve, 1000));
            }
          }
        }
      } catch (error) {
        console.error('[PaymentSuccess] Unexpected error during verification:', error);
      } finally {
        setVerifying(false);
        setVerificationComplete(true);
        setReadyToNavigate(true);
      }
    };

    verifyPaymentAndRefreshSubscription();
  }, []); // Empty dependency array - run only once on mount

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-secondary flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <CardTitle className="text-2xl font-bold text-green-700">Payment Successful!</CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-6">
          {verifying && !verificationComplete ? (
            <div className="space-y-4">
              <div className="flex items-center justify-center space-x-2">
                <Loader className="w-5 h-5 animate-spin text-primary" />
                <span className="text-sm text-muted-foreground">
                  Verifying payment and activating features...
                </span>
              </div>
            </div>
          ) : (
            <>
              <div className="space-y-2">
                <p className="text-gray-600">
                  Thank you for your payment. Your subscription has been activated and you now have access to all {verifiedTier || subscriptionTier || 'Starter'} features.
                </p>
                <p className="text-sm text-gray-500">
                  You will receive a confirmation email shortly with your receipt and subscription details.
                </p>
              </div>
              
              <div className="space-y-3">
                <Button 
                  onClick={() => navigate("/dashboard")} 
                  className="w-full"
                  disabled={!readyToNavigate || verifying}
                >
                  Go to Dashboard
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => navigate("/")}
                  className="w-full"
                  disabled={!readyToNavigate || verifying}
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Home
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccess;